/* eslint-disable react-hooks/rules-of-hooks */
import SearchIcon from '@mui/icons-material/Search'
import { v4 as uuidv4 } from 'uuid'
import {
  Stack,
  Button,
  Checkbox,
  DialogActions,
  DialogTitle,
  Divider,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material'
import { DialogContent } from '@mui/material'
import { BootstrapDialog } from 'components/BootstrapDialog'
import { DialogCloseButton } from 'components/DialogCloseButton'
import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useCreateTenderCommissionMutation } from 'services/tender/tenderCommissions'
import { useUsersQuery } from 'services/users'
import { ITender } from 'types/tender'
import { useRowSelection } from 'hooks/useRowSelection'
import { ITenderCommission } from 'types/tender/tenderCommissions'
import { useSearch } from 'hooks/useSearch'

type Props = {
  isOpen: boolean
  onClose: () => void
  tenderData?: ITender
  tenderCommission?: ITenderCommission
}

export const DialogAddCommitteeMembers = ({
  isOpen,
  onClose,
  tenderData,
  tenderCommission,
}: Props) => {
  const { t } = useTranslation()
  const [searchNameQuery, setSearchNameQuery] = useState('')
  const [searchEmailQuery, setSearchEmailQuery] = useState('')
  const [searchPinQuery, setSearchPinQuery] = useState('')

  const { query: debouncedSearchNameQuery } = useSearch(searchNameQuery)
  const { query: debouncedSearchEmailQuery } = useSearch(searchEmailQuery)
  const { query: debouncedSearchPinQuery } = useSearch(searchPinQuery)

  const args = {
    name: debouncedSearchNameQuery,
    email: debouncedSearchEmailQuery,
    pin: debouncedSearchPinQuery,
  } as const

  const { data: users } = useUsersQuery(args)

  const selectedUserIds = tenderCommission?.tenderCommissionMembers?.map(
    (member) => member.userId
  )

  const rowSelection = useRowSelection({
    totalRows: users?.info.totalRows || 0,
    defaultSelectedRows: selectedUserIds,
  })

  const [createTenderCommission, { isSuccess }] = useCreateTenderCommissionMutation()

  useEffect(() => {
    if (isSuccess) {
      onClose()
    }
  }, [onClose, isSuccess])

  const handleSelectedMembers = () => {
    const data = {
      id: tenderCommission?.id ?? uuidv4(),
      //TODO. Remove name field
      name: 'Dummy name',
      tenderId: tenderData?.id,
      tenderCommissionMembers: rowSelection.selectedRows.map((userId) => {
        const findAlreadyExistingMember =
          tenderCommission?.tenderCommissionMembers?.find(
            (member) => userId === member.userId
          )
        const id = findAlreadyExistingMember?.id || uuidv4()
        const chairman = findAlreadyExistingMember?.chairman || false

        return {
          id,
          chairman,
          userId,
        }
      }),
    }

    createTenderCommission(data)
  }

  return (
    <BootstrapDialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ m: 0, p: 2, fontWeight: 'bold' }}>
        {t('committee-members')}
      </DialogTitle>

      <DialogCloseButton handleClose={onClose} />

      <DialogContent dividers>
        <Stack direction="row" spacing={2}>
          <TextField
            label={t('search-based-on-name')}
            size="small"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            value={searchNameQuery}
            onChange={(event) => setSearchNameQuery(event.target.value)}
          />
          <TextField
            label={t('search-based-on-email')}
            size="small"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            value={searchEmailQuery}
            onChange={(event) => setSearchEmailQuery(event.target.value)}
          />
          <TextField
            label={t('search-based-on-fin')}
            size="small"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            value={searchPinQuery}
            onChange={(event) => setSearchPinQuery(event.target.value)}
          />
        </Stack>
        <Divider sx={{ my: 1.5 }} />
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    color="primary"
                    checked={rowSelection.headCellStatus === 'all'}
                    onChange={() => {
                      if (users === undefined) return

                      return rowSelection.toggleHeadCell(
                        users?.items.map((user) => user.id)
                      )
                    }}
                    inputProps={{
                      'aria-label': 'select all desserts',
                    }}
                  />
                </TableCell>

                <TableCell sx={{ fontWeight: 'bold' }}>
                  {t('name-surname')}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}>
                  {t('user-email-address')}
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{t('position')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{t('fin')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  {t('serial-number')}
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  {t('phone-number')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users?.items.map((user) => (
                <TableRow
                  key={user.id}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      color="primary"
                      checked={rowSelection.isChecked(user.id)}
                      onChange={() => rowSelection.toggle(user.id)}
                    />
                  </TableCell>
                  <TableCell>{`${user?.firstName} ${user.lastName}`}</TableCell>
                  <TableCell>{user?.email}</TableCell>
                  <TableCell>{}</TableCell>
                  <TableCell>{user?.personalIdentificationNumber}</TableCell>
                  <TableCell>{user.identityCardNumber}</TableCell>
                  <TableCell>{user?.phoneNumber}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions sx={{ mt: 3 }}>
        <Button
          onClick={handleSelectedMembers}
          disabled={rowSelection.headCellStatus === 'empty'}
          color="primary"
          variant="contained"
          style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
        >
          {t('add-selected')}
        </Button>
      </DialogActions>
    </BootstrapDialog>
  )
}
